import { useState } from 'react'
import { useFeedback, useMentors } from '@/lib/queries'
import type { FeedbackFilters } from '@/lib/queries'
import { PageHeader, Card, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pagination } from '@/components/ui/Pagination'
import { Pill } from '@/components/ui/Pill'
import { formatDate } from '@/lib/format'
import type { Feedback } from '@/types/api'
import shared from '@/features/bookings/BookingsPage.module.css'
import styles from './FeedbackPage.module.css'

/**
 * A15 — Feedback.
 *
 * *** THE DEFAULT VIEW IS EVERYTHING, BUT THE USEFUL VIEW IS LOW SCORES ***
 * Nobody acts on a five-star review. The "Needs attention" filter is a
 * first-class control rather than a rating dropdown buried among others,
 * because finding unhappy students quickly is the entire point of this screen.
 */
export function FeedbackPage() {
  const mentors = useMentors()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [mentorId, setMentorId] = useState('')
  const [onlyLow, setOnlyLow] = useState(false)

  const filters: FeedbackFilters = {
    page,
    page_size: pageSize,
    mentor_id: mentorId || undefined,
    max_rating: onlyLow ? 3 : undefined,
  }

  const { data, isLoading, isFetching, error, refetch } = useFeedback(filters)
  const hasFilters = Boolean(mentorId || onlyLow)

  const columns: Array<Column<Feedback>> = [
    {
      key: 'rating',
      header: 'Rating',
      width: '120px',
      render: (row) => <Stars rating={row.rating} />,
    },
    {
      key: 'comment',
      header: 'Comment',
      render: (row) =>
        row.comment ? (
          <p className={styles.comment}>{row.comment}</p>
        ) : (
          <span className={shared.muted}>No comment left</span>
        ),
    },
    {
      key: 'mentor',
      header: 'Mentor',
      render: (row) => (
        <div>
          <span className={shared.primary}>{row.mentor_name}</span>
          <p className={shared.secondary}>from {row.student_name}</p>
        </div>
      ),
    },
    {
      key: 'signals',
      header: '',
      secondary: true,
      render: (row) => (
        <div className={styles.signals}>
          {/*
            Only shown when FALSE. A "yes" here is the expected case and adds
            nothing; a "no" is the thing an admin needs to see.
          */}
          {row.mentor_joined_on_time === false && (
            <Pill tone="danger">Mentor was late</Pill>
          )}
          {row.problem_resolved === false && (
            <Pill tone="warning">Not resolved</Pill>
          )}
        </div>
      ),
    },
    {
      key: 'when',
      header: 'Session',
      align: 'right',
      secondary: true,
      render: (row) => (
        <span className={shared.secondary}>
          {formatDate(row.session_at ?? row.created_at)}
        </span>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Feedback"
        description="What students said after their sessions."
      />

      <Card flush>
        <div className={shared.filters}>
          <button
            type="button"
            onClick={() => {
              setOnlyLow((current) => !current)
              setPage(1)
            }}
            className={`${styles.toggle} ${onlyLow ? styles.toggleActive : ''}`}
            aria-pressed={onlyLow}
          >
            Needs attention (3 stars or fewer)
          </button>

          <select
            value={mentorId}
            onChange={(event) => {
              setMentorId(event.target.value)
              setPage(1)
            }}
            className={shared.select}
            aria-label="Filter by mentor"
          >
            <option value="">All mentors</option>
            {(mentors.data?.mentors ?? []).map((mentor) => (
              <option key={mentor.user_id} value={mentor.user_id}>
                {mentor.full_name}
              </option>
            ))}
          </select>

          {hasFilters && (
            <button
              type="button"
              className={shared.clear}
              onClick={() => {
                setMentorId('')
                setOnlyLow(false)
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </div>

        <Table
          columns={columns}
          rows={data?.feedback ?? []}
          rowKey={(row) => row.feedback_id}
          loading={isLoading || isFetching}
          error={error}
          onRetry={() => void refetch()}
          isFiltered={hasFilters}
          emptyMessage="No feedback yet. Students are asked after a session ends."
          emptyFilteredMessage={
            onlyLow
              ? 'No low ratings. Every review is 4 stars or better.'
              : 'No feedback matches these filters.'
          }
        />
      </Card>

      {data?.page && (
        <Pagination
          page={data.page}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}
    </PageStack>
  )
}

function Stars({ rating }: { rating: number }) {
  const tone = rating <= 2 ? styles.poor : rating === 3 ? styles.fair : styles.good

  return (
    <span
      className={`${styles.stars} ${tone}`}
      // The visual is stars; the accessible name is the number, because "★★★☆☆"
      // read aloud is noise.
      aria-label={`${rating} out of 5`}
      title={`${rating} out of 5`}
    >
      {'★'.repeat(rating)}
      <span className={styles.starsEmpty}>{'★'.repeat(5 - rating)}</span>
    </span>
  )
}
